import { api } from "./api";

/**
 * Web Push for new messages. The browser subscription is created only after the person asks for it; the server
 * keeps just the push-service endpoint and sends pushes without any content.
 */
export type PushAvailability = "unsupported" | "needs_home_screen" | "available" | "enabled" | "denied";

function standalone(): boolean {
  return window.matchMedia?.("(display-mode: standalone)").matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

function ios(): boolean {
  return /iPad|iPhone|iPod/u.test(navigator.userAgent)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function keyBytes(base64Url: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - base64Url.length % 4) % 4);
  const binary = atob(base64Url.replaceAll("-", "+").replaceAll("_", "/") + padding);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

type PushSpace = "Romantic" | "Friendship";
const scopeFor = (space: PushSpace) => space === "Friendship" ? "/friendship-push/" : "/";
async function registrationFor(space: PushSpace) {
  const scope=scopeFor(space), registration=await navigator.serviceWorker.getRegistration(scope);
  return registration && new URL(registration.scope).pathname===scope ? registration : undefined;
}

export async function pushAvailability(space: PushSpace = "Romantic"): Promise<PushAvailability> {
  const supported = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  if (!supported) return ios() && !standalone() ? "needs_home_screen" : "unsupported";
  const { publicKey } = await api<{ publicKey: string | null }>("/api/v1/pairing/push/config");
  if (!publicKey) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  const registration = await registrationFor(space);
  // A notification-only scope never controls a page, so the browser rarely re-checks its script on its own.
  await registration?.update().catch(() => undefined);
  const subscription = await registration?.pushManager.getSubscription();
  if (subscription && Notification.permission === "granted") {
    // Keep the server copy in step with this device (a cleared server row or a rotated endpoint).
    await api("/api/v1/pairing/push/subscription", { method: "PUT", body: JSON.stringify({ endpoint: subscription.endpoint, space }) }).catch(() => undefined);
    return "enabled";
  }
  return "available";
}

export async function enablePush(space: PushSpace = "Romantic"): Promise<PushAvailability> {
  const { publicKey } = await api<{ publicKey: string | null }>("/api/v1/pairing/push/config");
  if (!publicKey) return "unsupported";
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return permission === "denied" ? "denied" : "available";
  const registration = await navigator.serviceWorker.register(space === "Friendship" ? "/sw.js?space=Friendship" : "/sw.js", { scope: scopeFor(space) });
  // A notification-only scope need not control this page. Wait for its own activation.
  if (!registration.active) await new Promise<void>((resolve,reject)=>{
    const worker=registration.installing??registration.waiting;
    if(!worker){reject(new Error("通知暂时无法开启"));return;}
    const changed=()=>{if(worker.state==='activated'){worker.removeEventListener('statechange',changed);resolve();}else if(worker.state==='redundant'){worker.removeEventListener('statechange',changed);reject(new Error("通知暂时无法开启"));}};
    worker.addEventListener('statechange',changed);changed();
  });
  const subscription = await registration.pushManager.getSubscription()
    ?? await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) });
  await api("/api/v1/pairing/push/subscription", { method: "PUT", body: JSON.stringify({ endpoint: subscription.endpoint, space }) });
  return "enabled";
}

/** An explicit toggle for one space; the other registration and subscription are untouched. */
export async function disablePushForSpace(space: PushSpace): Promise<PushAvailability> {
  if (!("serviceWorker" in navigator)) return "unsupported";
  const registration = await registrationFor(space);
  const subscription = await registration?.pushManager?.getSubscription();
  if (subscription) {
    await api("/api/v1/pairing/push/subscription", { method: "DELETE", body: JSON.stringify({ endpoint: subscription.endpoint }) });
    if (!await subscription.unsubscribe()) throw new Error("通知暂时无法关闭，请重试。");
  }
  return "available";
}

/** On logout this device stops receiving notifications for the account that is leaving. */
export async function disablePushForThisDevice(): Promise<void> {
  if (!("serviceWorker" in navigator)) return;
  for(const space of ["Romantic","Friendship"] as const) {
    const registration = await registrationFor(space);
    const subscription = await registration?.pushManager?.getSubscription();
    if (!subscription) continue;
    await api("/api/v1/pairing/push/subscription", { method: "DELETE", body: JSON.stringify({ endpoint: subscription.endpoint }) }).catch(() => undefined);
    await subscription.unsubscribe().catch(() => false);
  }
}
